import { useState, useEffect, memo } from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Edit,
  Trash2,
  Calendar,
  Clock,
  User,
  Phone,
  Mail,
} from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import ReservationEditModal from './ReservationEditModal';

interface ChangeHistoryItem {
  timestamp: string;
  action?: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  userId?: string;
  changes?: {
    date?: string;
    startTime?: string;
    duration?: number;
    staff?: string;
    service?: string;
  };
}

interface VisitHistoryItem {
  date: string;
  serviceId: string;
  serviceName: string;
  staffId: string;
  staffName: string;
  price: number;
  notes?: string;
}

interface MedicalInfo {
  allergies?: string[];
  medications?: string[];
  conditions?: string[];
  notes?: string;
  lastUpdated?: string;
}

interface MedicalRecord {
  id: string;
  date: string;
  type: 'consultation' | 'treatment' | 'follow-up';
  description: string;
  recommendations?: string;
  nextVisit?: string;
  createdBy: string;
}

interface Reservation {
  id: string;
  customerId: number;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  staffId: string;
  staffName: string;
  serviceId: string;
  serviceName: string;
  date: Date;
  startTime: string;
  endTime: string;
  duration: number;
  status: 'confirmed' | 'pending' | 'cancelled';
  notes?: string;
  color: string;
  changeHistory?: ChangeHistoryItem[];
  isVip?: boolean;
  price?: number;
}

interface ExtendedCustomer {
  id: number;
  name: string;
  nameKana?: string;
  email: string;
  phone: string;
  birthday?: string;
  occupation?: string;
  address?: string;
  preferences?: string;
  lastVisit: string;
  totalVisits: number;
  totalSpent: number;
  status: 'active' | 'inactive';
  tags: string[];
  visitHistory?: VisitHistoryItem[];
  medicalInfo?: MedicalInfo;
  medicalRecords?: MedicalRecord[];
  createdAt: string;
  updatedAt: string;
  birthDate?: string;
  gender?: 'male' | 'female' | 'other' | 'unspecified';
  firstVisitDate?: string;
  bookingSource?: string;
  memo?: string;
  phoneSecondary?: string;
}

interface ExtendedAppointmentDetailsProps {
  appointment: Reservation;
  onEdit?: () => void;
  onDelete?: () => void;
  onUpdate?: (updatedReservation: Reservation) => void;
}


const isPremiumCustomer = (isVip?: boolean): boolean => {
  return Boolean(isVip);
};

// 顧客名とフリガナのマッピングテーブル
const customerNameKanaMap: Record<string, string> = {
  '山田 花子': 'ヤマダ ハナコ',
  '佐藤 美咲': 'サトウ ミサキ',
  '田中 愛': 'タナカ アイ',
  '鈴木 真理子': 'スズキ マリコ',
  '高橋 優子': 'タカハシ ユウコ',
  '伊藤 恵子': 'イトウ ケイコ',
  '渡辺 美穂': 'ワタナベ ミホ',
  '中村 由美': 'ナカムラ ユミ',
  '田中 美咲': 'タナカ ミサキ',
};

// モック顧客詳細データ取得関数
const fetchCustomerDetails = async (customerId: number, customerName: string): Promise<ExtendedCustomer> => {
  await new Promise(resolve => setTimeout(resolve, 300)); // 遅延シミュレーション

  // 顧客名からフリガナを取得、存在しない場合はデフォルト
  const nameKana = customerNameKanaMap[customerName] || 'フリガナ 未設定';

  const mockCustomerData: ExtendedCustomer = {
    id: customerId,
    name: customerName,
    nameKana: nameKana,
    email: 'customer@example.com',
    phone: '090-1234-5678',
    birthday: '1985-05-15',
    occupation: 'マーケティング',
    address: '東京都渋谷区',
    preferences: '自然派製品希望、香りは控えめ',
    lastVisit: '2024-01-15',
    totalVisits: 15,
    totalSpent: 180000,
    status: 'active',
    tags: ['VIP', '紹介顧客', 'リピーター'],
    visitHistory: [],
    medicalInfo: {},
    medicalRecords: [],
    createdAt: '2023-01-10',
    updatedAt: '2024-01-15',
    birthDate: '1985-05-15',
    gender: 'female',
    firstVisitDate: '2023-01-10',
    bookingSource: 'ホットペッパービューティー',
    memo: '毎回同じスタイリストを希望。カットは短めが好み。',
    phoneSecondary: '03-1234-5678',
  };

  return mockCustomerData;
};

const ExtendedAppointmentDetails = memo<ExtendedAppointmentDetailsProps>(({
  appointment,
  onEdit,
  onDelete,
  onUpdate
}) => {
  const [customerDetails, setCustomerDetails] = useState<ExtendedCustomer | null>(null);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);

  useEffect(() => {
    const loadCustomerDetails = async () => {
      setLoading(true);
      try {
        const details = await fetchCustomerDetails(appointment.customerId, appointment.customerName);
        setCustomerDetails(details);
      } catch (error) {
        console.error('Failed to load customer details:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCustomerDetails();
  }, [appointment.customerId, appointment.customerName]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[300px]">
        <p className="text-sm text-muted-foreground">
          読み込み中...
        </p>
      </div>
    );
  }

  const isPremium = isPremiumCustomer(appointment.isVip);

  return (
    <div className="space-y-4">
      {/* 基本情報表示 */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <Avatar
            className={`h-12 w-12 ${
              isPremium
                ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-gray-900'
                : ''
            }`}
            style={!isPremium ? { backgroundColor: appointment.color } : undefined}
          >
            <AvatarFallback>
              <User className="h-6 w-6" />
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="flex items-center gap-1">
              <h3 className="text-lg font-bold">
                {appointment.customerName}
              </h3>
            </div>
            {customerDetails?.nameKana && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {customerDetails.nameKana}
              </p>
            )}
            <div className="flex items-center gap-0.5 mt-1">
              <Badge variant="secondary" className="text-sm font-semibold">
                {appointment.serviceName}
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setEditModalOpen(true);
              if (onEdit) {
                onEdit();
              }
            }}
            className="min-h-[44px]"
          >
            <Edit className="mr-2 h-4 w-4" />
            編集
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onDelete}
            className="min-h-[44px]"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            削除
          </Button>
        </div>
      </div>

      {/* 顧客情報グリッド */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div>
          <p className="text-xs font-medium text-muted-foreground">
            総来店回数
          </p>
          <p className="text-sm mt-0.5">
            {customerDetails?.totalVisits || 0}回
          </p>
        </div>
        <div>
          <p className="text-xs font-medium text-muted-foreground">
            総消費額
          </p>
          <p className="text-sm font-medium mt-0.5">
            ¥{customerDetails?.totalSpent?.toLocaleString() || 0}
          </p>
        </div>
        <div>
          <p className="text-xs font-medium text-muted-foreground">
            前回来店
          </p>
          <p className="text-sm mt-0.5">
            {customerDetails?.lastVisit ? format(new Date(customerDetails.lastVisit), 'yyyy年M月d日', { locale: ja }) : '未記録'}
          </p>
        </div>
      </div>


      {/* その他顧客詳細情報 */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        {customerDetails?.occupation && (
          <div>
            <p className="text-xs font-medium text-muted-foreground">
              職業
            </p>
            <p className="text-sm mt-0.5">
              {customerDetails.occupation}
            </p>
          </div>
        )}
        {customerDetails?.bookingSource && (
          <div>
            <p className="text-xs font-medium text-muted-foreground">
              予約経路
            </p>
            <p className="text-sm mt-0.5">
              {customerDetails.bookingSource}
            </p>
          </div>
        )}
      </div>

      <Separator className="my-2" />

      {/* 予約詳細情報 */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div>
          <p className="text-xs font-medium text-muted-foreground">
            日時
          </p>
          <div className="flex items-center gap-1 mt-0.5">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm">
              {format(appointment.date, 'yyyy年M月d日', { locale: ja })} {appointment.startTime}
            </p>
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-muted-foreground">
            所要時間
          </p>
          <div className="flex items-center gap-1 mt-0.5">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm">{appointment.duration}分</p>
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-muted-foreground">
            担当スタッフ
          </p>
          <div className="flex items-center gap-1 mt-0.5">
            <User className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm">{appointment.staffName}</p>
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-muted-foreground">
            料金
          </p>
          <p
            className={`text-sm font-medium mt-0.5 ${
              isPremium ? 'text-orange-500' : ''
            }`}
          >
            ¥{appointment.price?.toLocaleString()}
          </p>
        </div>
      </div>

      {/* 連絡先情報 */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div>
          <p className="text-xs font-medium text-muted-foreground">
            電話番号
          </p>
          <div className="flex items-center gap-1 mt-0.5">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm">{customerDetails?.phone}</p>
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-muted-foreground">
            メールアドレス
          </p>
          <div className="flex items-center gap-1 mt-0.5">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <p className="text-xs">
              {customerDetails?.email}
            </p>
          </div>
        </div>
      </div>

      {/* 顧客メモ */}
      {customerDetails?.memo && (
        <div className="mb-3">
          <p className="text-xs font-medium text-muted-foreground">
            顧客メモ
          </p>
          <div className="mt-1 p-2 bg-muted rounded-md border">
            <p className="text-sm whitespace-pre-wrap">
              {customerDetails.memo}
            </p>
          </div>
        </div>
      )}

      {/* 予約メモ */}
      {appointment.notes && (
        <div className="mb-3">
          <p className="text-xs font-medium text-muted-foreground">
            予約メモ
          </p>
          <div className="mt-1 p-2 bg-muted rounded-md">
            <p className="text-sm whitespace-pre-wrap">{appointment.notes}</p>
          </div>
        </div>
      )}

      {/* 編集モーダル */}
      <ReservationEditModal
        open={editModalOpen}
        reservation={appointment}
        onClose={() => setEditModalOpen(false)}
        onSave={(updatedReservation) => {
          console.log('予約が更新されました:', updatedReservation);
          setEditModalOpen(false);
          if (onUpdate) {
            onUpdate(updatedReservation);
          }
        }}
      />
    </div>
  );
});

ExtendedAppointmentDetails.displayName = 'ExtendedAppointmentDetails';

export default ExtendedAppointmentDetails;
